import { CircularProgress, InputAdornment, TextField } from "@mui/material";
import clsx from "clsx";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import type { IListarGruposUsuarioItem } from "@/api/space/dtos/authenticator.dto";
import useUser from "@auth/user/useUser";

export type SidebarGruposModalProps = {
	readonly grupos: IListarGruposUsuarioItem[];
	readonly totalGrupos: number;
	readonly carregando: boolean;
	readonly buscaGrupo: string;
	readonly grupoSelecionadoId: number | null;
	readonly temAlteracoes: boolean;
	readonly fluido?: boolean;
	readonly onBuscar: (valor: string) => void;
	readonly onSelecionar: (id: number) => void;
};

type ItemGrupoProps = {
	readonly grupo: IListarGruposUsuarioItem;
	readonly selecionado: boolean;
	readonly temAlteracoes: boolean;
	readonly onSelecionar: (id: number) => void;
};

function ItemGrupo({ grupo, selecionado, temAlteracoes, onSelecionar }: ItemGrupoProps) {
	const user = useUser();

	const isMesmoGrupo = grupo.id === user?.data?.id_cad_grupo_usuario;

	return (
		<button
			type="button"
			onClick={() => onSelecionar(grupo.id)}
			className={clsx(
				"relative flex w-full items-center gap-3 rounded-xl px-3 py-1 text-left transition-colors",
				selecionado ? "bg-primary/10" : "hover:bg-muted/60 hover:cursor-pointer",
			)}
		>
			<span
				className={clsx(
					"flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-extrabold",
					selecionado
						? "bg-primary text-primary-foreground"
						: "border border-info/50 bg-muted text-muted-foreground",
				)}
			>
				{grupo.id}
			</span>

			<span className="flex min-w-0 flex-1 flex-col">
				<span
					className={clsx(
						"truncate text-sm font-bold uppercase",
						selecionado ? "text-primary" : "text-foreground",
					)}
				>
					{grupo.descricao}
				</span>
				<span
					className={clsx(
						"truncate text-xs font-medium text-muted-foreground",
						selecionado ? "text-primary underline" : "",
					)}
				>
					{isMesmoGrupo ? "Meu grupo" : ""}
				</span>
			</span>

			{selecionado && temAlteracoes ? (
				<span title="Alterações não salvas" className="h-2 w-2 shrink-0 rounded-full bg-danger" />
			) : null}

			{selecionado ? (
				<span className="absolute left-0.5 top-1/2 h-7 w-1 -translate-y-1/2 rounded-full bg-primary" />
			) : null}
		</button>
	);
}

export default function SidebarGruposModal({
	grupos,
	totalGrupos,
	carregando,
	buscaGrupo,
	grupoSelecionadoId,
	temAlteracoes,
	fluido = false,
	onBuscar,
	onSelecionar,
}: SidebarGruposModalProps) {
	return (
		<aside
			className={clsx(
				"flex shrink-0 flex-col bg-paper",
				fluido ? "h-full w-full" : "h-60 border-b md:h-full md:w-70 md:border-b-0 md:border-r",
			)}
		>
			<div className="flex shrink-0 items-center gap-2 pb-3 pt-4">
				<span className="text-md font-semibold uppercase text-muted-foreground">Grupos</span>
				<span className="rounded-full bg-primary/10 px-2 text-sm font-bold text-primary">{totalGrupos}</span>
			</div>

			<div className="shrink-0 px-3 pb-3">
				<TextField
					value={buscaGrupo}
					onChange={(evento) => onBuscar(evento.target.value)}
					placeholder="Buscar grupo…"
					size="small"
					fullWidth
					autoFocus
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<FuseSvgIcon size={18}>lucide:search</FuseSvgIcon>
								</InputAdornment>
							),
						},
					}}
				/>
			</div>

			<div className="min-h-0 flex-1 overflow-y-auto pb-3">
				{carregando ? (
					<div className="flex items-center justify-center gap-2 py-8 text-sm font-semibold text-muted-foreground">
						<CircularProgress size={18} />
						Carregando grupos…
					</div>
				) : (
					<div className="flex flex-col gap-1">
						{grupos.map((grupo) => (
							<ItemGrupo
								key={grupo.id}
								grupo={grupo}
								selecionado={grupo.id === grupoSelecionadoId}
								temAlteracoes={temAlteracoes}
								onSelecionar={onSelecionar}
							/>
						))}

						{grupos.length === 0 ? (
							<p className="py-8 text-center text-sm font-semibold text-muted-foreground">
								Nenhum grupo encontrado.
							</p>
						) : null}
					</div>
				)}
			</div>
		</aside>
	);
}
